import { useEffect } from 'react';
import { useForecastMap, mapAlive } from './ForecastMapContext';
import { tileTemplate } from '../../lib/overlays/urls';

const SOURCE_ID = 'pg_points';
const BARB_LAYER = 'pg_points:barbs';
const LABEL_LAYER = 'pg_points:labels';

// Layouts copied verbatim from the captured Skysight style
// (docs/skysight-overlay-styles/*.json — the pg_points / pg_points_label layers).
const barbLayout = (sourceLayer) => ({
    'icon-image': ['case', ['>', ['get', 'wspd'], 0], ['get', 'wspd'], ''],
    'icon-rotate': ['to-number', ['get', 'wdir']],
    'icon-size': ['step', ['zoom'], 0, 9.5, 0.7],
    'icon-ignore-placement': true,
    'icon-allow-overlap': true,
    'icon-rotation-alignment': 'map',
    'icon-anchor': 'bottom',
    'icon-offset': [3, 3],
});
const labelLayout = {
    'icon-image': ['step', ['zoom'], '0', 7.5, ['concat', 'pg_point_', ['get', 'launch']]],
    'icon-size': ['step', ['zoom'], 0.6, 7.5, 0.22, 8.5, 0.28, 9, 0.32, 10, 0.35],
    'text-optional': true,
    'icon-allow-overlap': true,
    'text-allow-overlap': false,
    'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
    'text-padding': 3,
    'text-field': ['step', ['zoom'], '', 5, '', 7.5, ['get', 'short_name'], 9, ['get', 'long_name']],
    'text-max-width': 8,
    'text-line-height': 1.1,
    'text-anchor': 'top',
    'text-size': { stops: [[0, 0], [7, 11], [16, 15]] },
    'text-offset': { stops: [[8, [0, 0.45]], [9, [0, 0.9]]] },
};
const labelPaint = {
    'text-color': '#ffffff',
    'text-halo-blur': 0.5,
    'text-halo-color': '#333238',
    'text-halo-width': 0.5,
};

// Launch sites are always shown (matching Skysight, where pg_points is a
// persistent layer on every view).
const LaunchSitesLayer = ({ region, date, time }) => {
    const map = useForecastMap();

    useEffect(() => {
        if (!map) return undefined;
        const sourceLayer = time;

        if (!map.getSource(SOURCE_ID)) {
            map.addSource(SOURCE_ID, {
                type: 'vector',
                tiles: [tileTemplate({ region, date, time, param: 'pg_points' })],
                minzoom: 3,
                // Skysight serves pg_points only to z5; MapLibre overzooms for
                // closer views. z6 returns 204 (no tile) → nothing renders.
                maxzoom: 5,
            });
        }
        if (!map.getLayer(BARB_LAYER)) {
            map.addLayer({
                id: BARB_LAYER, type: 'symbol', source: SOURCE_ID,
                'source-layer': sourceLayer, layout: barbLayout(sourceLayer), minzoom: 4,
            });
        }
        if (!map.getLayer(LABEL_LAYER)) {
            map.addLayer({
                id: LABEL_LAYER, type: 'symbol', source: SOURCE_ID,
                'source-layer': sourceLayer, layout: labelLayout, paint: labelPaint, minzoom: 4,
            });
        }

        return () => {
            if (!mapAlive(map)) return; // map already destroyed (route unmount)
            [LABEL_LAYER, BARB_LAYER].forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
            if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
        };
    }, [map, region, date, time]);

    return null;
};

export default LaunchSitesLayer;
